import {
    TicketSeatsSuccessResponseResponseTicketSeatsItem,
    TicketTypesSuccessResponseResponseTicketTypesItem,
} from '../../openapi/model';
import { ISeat } from '../hooks/useCart';

export const COLOR_BUCKETS: string[] = [
    '#aec7e8', // light blue
    '#ff7f0e', // orange
    '#ffbb78', // light orange
    '#2ca02c', // green
    '#98df8a', // light green
    '#d62728', // red
    '#ff9896', // light red
    '#9467bd', // purple
    '#c5b0d5', // light purple
    '#8c564b', // brown
    '#c49c94', // light brown
    '#e377c2', // pink
    '#f7b6d2', // light pink
    '#7f7f7f', // gray
    '#c7c7c7', // light gray
    '#bcbd22', // yellow‑green
    '#dbdb8d', // light yellow‑green
    '#17becf', // cyan
    '#9edae5', // light cyan
    '#1f77b4', // blue
];

export const SELECTED_SEAT_COLOR = '#ffff00'; // yellow
export const SUGGESTED_SEAT_COLOR = '#ff8c00'; // vibrant dark orange
export const RESERVED_SEAT_COLOR = '#000000'; // black
export const DISABLED_SEAT_COLOR = '#bdbdbd'; // gray
export const DEFAULT_SEAT_COLOR = '#ffffff'; // not bookable white
export const FALLBACK_SEAT_COLOR = '#eeeeee'; // light gray fallback color

// Fill opacity for a seat that is free but not selectable with the current
// cart: it keeps its ticket type's colour and fades, so the hue still says
// which block it is. Mixing black into the colour instead does not work - it
// moves each hue somewhere else perceptually (yellow turns brown, reading as a
// different ticket type) and walks dark colours onto RESERVED_SEAT_COLOR, so
// "not for your ticket type" starts to look like "already taken".
export const DIMMED_SEAT_OPACITY = 0.3;

export function calculateColorMap(
    ticketTypes: TicketTypesSuccessResponseResponseTicketTypesItem[] | undefined,
    allSeats: TicketSeatsSuccessResponseResponseTicketSeatsItem[] | undefined,
    seatNeeds: { [key: string]: number },
    seats: ISeat[] | undefined,
    freeSeats: TicketSeatsSuccessResponseResponseTicketSeatsItem[] | undefined,
    suggestedSeatIds: Set<number> = new Set(),
): {
    colorMap: { [key: string]: string };
    ticketTypeColorMap: { [key: string]: string };
    ticketTypeNameI18nMap: { [key: string]: string };
    dimmedSeatIds: Set<number>;
} {
    const colorMap: { [key: string]: string } = {};
    const ticketTypeColorMap: { [key: string]: string } = {};
    const ticketTypeNameI18nMap: { [key: string]: string } = {};
    const freeSeatIds = freeSeats ? new Set(freeSeats.map((seat) => seat.id)) : new Set();
    // Seats coloured but not selectable with this cart. Tracked so the suggested
    // pass below can skip them: they now carry a colour where they previously
    // carried none, and a seat you cannot select must not be offered as a
    // suggestion.
    const dimmedSeatIds = new Set<number>();

    // each ticket type a random color
    if (ticketTypes) {
        ticketTypes.forEach((ticketType, i) => {
            let seatColor = COLOR_BUCKETS[i % COLOR_BUCKETS.length];

            // ticket type has a specific color set, use that
            if (ticketType.color) {
                const parsedColor = ticketType.color.trim();
                // validate hex color
                if (/^#([0-9A-F]{3}){1,2}$/i.test(parsedColor)) {
                    seatColor = parsedColor;
                } else {
                    // extend with a # if missing and validate again
                    if (/^([0-9A-F]{3}){1,2}$/i.test(parsedColor)) {
                        seatColor = `#${parsedColor}`;
                    } else {
                        // invalid color format, ignore
                        console.warn(
                            `Invalid color format for ticket type "${ticketType.name}": ${ticketType.color}`,
                        );
                    }
                }
            }

            // only add to ticketTypeColorMap if not a default color AND seats are associated with the ticket type
            if (
                seatColor !== RESERVED_SEAT_COLOR &&
                ticketType.seats &&
                ticketType.seats.length > 0
            ) {
                ticketTypeColorMap[ticketType.name] = seatColor;
                ticketTypeNameI18nMap[ticketType.name] = ticketType.nameI18n || ticketType.name;
            }

            const hasMatchingTicketType = (seatNeeds[ticketType.id] ?? 0) > 0;
            ticketType.seats?.forEach((seat) => {
                const seatId = Number(seat.id);
                if (!freeSeatIds.has(seat.id)) {
                    colorMap[seatId] = RESERVED_SEAT_COLOR;
                } else {
                    // Free either way. The seat keeps its ticket type's colour
                    // even when the cart cannot select it - the map is only
                    // legible if a block keeps one colour - and the renderer
                    // fades it instead of it dropping to the flat grey below.
                    colorMap[seatId] = seatColor;
                    if (!hasMatchingTicketType) dimmedSeatIds.add(seatId);
                }
            });
        });
    }
    if (allSeats) {
        allSeats.forEach((seat) => {
            const seatId = Number(seat.id);
            // check if seat is in the cart
            const isInCart = seats?.some((cartSeat) => Number(cartSeat.id) === seatId);
            if (isInCart) {
                colorMap[seatId] = SELECTED_SEAT_COLOR; // yellow
            }
            // suggested seats get orange color (only if not already selected)
            if (
                !isInCart &&
                suggestedSeatIds.has(seatId) &&
                colorMap[seatId] &&
                colorMap[seatId] !== RESERVED_SEAT_COLOR &&
                !dimmedSeatIds.has(seatId)
            ) {
                colorMap[seatId] = SUGGESTED_SEAT_COLOR;
            }
            if (!colorMap[seatId]) {
                colorMap[seatId] =
                    seat.bookable && freeSeatIds.has(seat.id)
                        ? DISABLED_SEAT_COLOR
                        : DEFAULT_SEAT_COLOR;
            }
        });
    }

    return { colorMap, ticketTypeColorMap, ticketTypeNameI18nMap, dimmedSeatIds };
}
